import React from 'react';
import { FiPercent } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const PercentageRatioLesson = () => {
  const sections = [
    {
      id: 'percentage-basics',
      title: 'Percentage Basics',
      content: {
        introduction:
          'A percentage is a fraction out of 100. Understanding percentage calculations is essential for solving many GRE quantitative problems.',

        concepts: [
          {
            title: 'Converting Between Formats',
            prerequisiteNote:
              'Review fractions and decimals in Arithmetic Foundations first.',
            vocabulary: ['fraction', 'decimal', 'percent', 'out of 100'],
            explanation:
              'Master the conversion between fractions, decimals, and percentages using basic multiplication and division.',
            microExample: {
              problem: 'Convert §1/4§ to a percent',
              solution: [
                '§1 ÷ 4 = 0.25§',
                '§0.25 × 100 = 25%§',
              ],
            },
            greTrap:
              'Percent means out of 100. A 15% value is §0.15§, not §15§.',
            checkQuestion: 'What decimal equals 40%?',
            example: {
              problem: 'Convert §3/5§ to a percentage',
              solution: [
                'Convert fraction to decimal: §3 ÷ 5 = 0.6§',
                'Convert decimal to percentage: §0.6 × 100 = 60%§',
                'Alternative: §(3/5) × 100 = 300/5 = 60§%',
                'Answer: 60%',
              ],
            },
          },
          {
            title: 'Finding Parts, Wholes, and Percentages',
            explanation:
              'Use the three fundamental percentage formulas:\n• §Part = (Percentage/100) × Whole§\n• §Percentage = (Part/Whole) × 100§\n• §Whole = Part ÷ (Percentage/100)§',
            example: {
              problem: 'What is 25% of 80?',
              solution: [
                'Use the formula: §Part = (Percentage/100) × Whole§',
                'Substitute values: §Part = (25/100) × 80§',
                'Simplify: §Part = 0.25 × 80 = 20§',
                'Answer: 20',
              ],
            },
          },
          {
            title: 'Working Backwards from Percentages',
            explanation:
              'When given a percentage and the resulting value, you can find the original whole using division.',
            example: {
              problem: 'If 15 is 30% of a number, what is the number?',
              solution: [
                'Let x be the unknown number',
                'Set up equation: 30% of §x = 15§, so §0.3x = 15§',
                'Solve for x: §x = 15 ÷ 0.3 = 50§',
                'Answer: 50',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'percentage-change',
      title: 'Percentage Change',
      content: {
        introduction:
          'Percentage change calculations show how quantities increase or decrease relative to their original values.',

        concepts: [
          {
            title: 'Percentage Increase and Decrease',
            explanation:
              'Percentage Change = §\\left[ \\frac{New Value - Original Value}{Original Value} \\right] \\times 100%§\nPositive results indicate increases, negative results indicate decreases.',
            example: {
              problem:
                'A shirt originally costs $40. It goes on sale for $32. What is the percentage decrease?',
              solution: [
                'Calculate the change: $32 - $40 = -$8',
                'Apply formula: §(-8/40) × 100§',
                'Simplify: §-0.2 × 100 = -20%§',
                'Answer: 20% decrease',
              ],
            },
          },
          {
            title: 'Successive Percentage Changes',
            explanation:
              'When multiple percentage changes occur, use the formula: Overall change = §p + q + (pq/100)§ where p and q are the individual percentage changes.',
            example: {
              problem:
                'A number increases by 20%, then decreases by 15%. What is the overall percentage change?',
              solution: [
                'Identify changes: p = 20%, q = -15%',
                'Apply formula: §20 + (-15) + (20×(-15))/100§',
                'Calculate: §5 + (-300/100) = 5 - 3 = 2§',
                'Answer: 2% increase',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'ratios-proportions',
      title: 'Ratios & Proportions',
      content: {
        introduction:
          'Ratios compare quantities, while proportions state that two ratios are equal. These concepts are crucial for solving many GRE problems.',

        concepts: [
          {
            title: 'Understanding Ratios',
            prerequisiteNote:
              'A ratio compares parts; make sure you know what each part represents.',
            vocabulary: ['ratio', 'part', 'whole', 'scale factor'],
            explanation:
              "A ratio a:b means 'a to b' and can be written as §a/b§. Ratios can be simplified by dividing both terms by their GCD.",
            microExample: {
              problem: 'The ratio of red to blue is 2:3. How many total parts?',
              solution: ['Total parts: §2 + 3 = 5§'],
            },
            greTrap:
              'A ratio part is not always the real quantity; multiply by the scale factor.',
            checkQuestion:
              'If 2 ratio parts represent 10 people, what does 1 part represent?',
            example: {
              problem:
                'In a class, the ratio of boys to girls is 3:4. If there are 21 students total, how many are boys?',
              solution: [
                'Total ratio parts: §3 + 4 = 7§',
                'Each part represents: §21 ÷ 7 = 3§ students',
                'Number of boys: 3 parts × 3 students per part = §9§',
                'Answer: 9 boys',
              ],
            },
          },
          {
            title: 'Solving Proportions',
            explanation:
              'If §a/b = c/d§, then §ad = bc§ (cross multiplication). Use this to solve for unknown values in proportional relationships.',
            example: {
              problem: 'If 3 apples cost $2, how much do 15 apples cost?',
              solution: [
                'Set up proportion: §3/2 = 15/x§ (apples to dollars)',
                'Cross multiply: §3x = 2 × 15 = 30§',
                'Solve for x: §x = 30 ÷ 3 = 10§',
                'Answer: $10',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'applications',
      title: 'Real-World Applications',
      content: {
        introduction:
          'Apply percentage and ratio concepts to solve complex problems involving mixtures, interest, and business scenarios.',

        concepts: [
          {
            title: 'Mixture Problems',
            explanation:
              'Use the concept that (concentration₁ × amount₁) + (concentration₂ × amount₂) = (final concentration × total amount).',
            example: {
              problem:
                'A 20% alcohol solution is mixed with a 60% alcohol solution to create 100ml of 35% solution. How much of each solution is needed?',
              solution: [
                'Let x = ml of 20% solution, then (100-x) = ml of 60% solution',
                'Set up equation: 0.2x + 0.6(100-x) = 0.35(100)',
                'Solve: 0.2x + 60 - 0.6x = 35',
                '-0.4x = -25, so x = 62.5ml of 20% solution',
                'Answer: 62.5ml of 20%, 37.5ml of 60%',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'After a 20% discount, a jacket costs $64. What was the original price?',
      options: ['$80', '$76.80', '$84', '$70'],
      correct: 0,
      explanation:
        'If $64 is 80% of original price, then original = $64 ÷ 0.8 = $80',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'What is 15% of 240?',
      options: ['32', '36', '40', '45'],
      correct: 1,
      explanation: '15% of 240 = 0.15 × 240 = 36',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'In a ratio of 5:3, if the larger quantity is 20, what is the smaller quantity?',
      options: ['10', '12', '15', '18'],
      correct: 1,
      explanation:
        'If 5 corresponds to 20, then the ratio factor is §20/5 = 4§. So the smaller quantity is §3 × 4 = 12§',
    },
    {
      type: 'timed',
      timeLimit: 95,
      question:
        'A number increases by 25% and then decreases by 20%. What is the overall percentage change?',
      options: ['0%', '5% increase', '5% decrease', '10% increase'],
      correct: 0,
      explanation:
        'Let original = 100. After 25% increase: 125. After 20% decrease: 125 × 0.8 = 100. No change overall.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'If §3/4§ of a number is 60, what is §7/8§ of the same number?',
      options: ['50', '70', '75', '80'],
      correct: 1,
      explanation:
        'If §(3/4) × x = 60§, then §x = 60 × (4/3) = 80§. So §(7/8) × 80 = 70§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A store increases the price of an item by 30%, then decreases it by 20%. What is the net change in price?',
      options: [
        '4% increase',
        '10% decrease',
        '4% decrease',
        '10% increase',
        'No change',
      ],
      correct: 0,
      explanation:
        'Start §100§. After 30% increase: §100 × 1.3 = 130§. After 20% decrease: §130 × 0.8 = 104§. Net change = §4%§ increase.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'In a class, the ratio of boys to girls is 3:5. If there are 40 students total, how many girls are there?',
      options: ['25', '20', '15', '30', '18'],
      correct: 0,
      explanation: 'Total parts = §3 + 5 = 8§. Girls = §(5/8) × 40 = 25§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'If 40% of a number is 80, what is 25% of the same number?',
      options: ['50', '60', '70', '80'],
      correct: 0,
      explanation:
        'If 40% of x = 80, then x = 80/0.4 = 200. So 25% of 200 = §0.25 × 200 = 50§',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A price increases from $50 to $65. What is the percentage increase?',
      options: ['15%', '20%', '25%', '30%'],
      correct: 3,
      explanation:
        'Increase = $65 - $50 = $15. Percentage increase = §(15/50) × 100 = 30%§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a class of 35 students, the ratio of boys to girls is 3:4. How many girls are there?',
      options: ['15', '20', '21', '28'],
      correct: 1,
      explanation:
        'Total ratio parts: §3 + 4 = 7§. Each part = §35/7 = 5§ students. Girls = 4 parts × 5 = 20.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What percentage is equivalent to the fraction §7/8§?',
      options: ['82.5%', '85%', '87.5%', '90%'],
      correct: 2,
      explanation: '§7/8 = 0.875 = 87.5%§',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'If a recipe calls for ingredients in the ratio 2:3:4, and you use 6 cups of the first ingredient, how many total cups do you need?',
      options: ['18', '24', '27', '30'],
      correct: 2,
      explanation:
        'If first ingredient (2 parts) = 6 cups, then each part = 3 cups. Total = (2+3+4) × 3 = 27 cups.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'What is 15% of 80?',
      options: ['12', '10', '15', '20', '8'],
      correct: 0,
      explanation: '§15% × 80 = 0.15 × 80 = 12§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'If §x%§ of 200 is 50, what is §x§?',
      options: ['25', '20', '30', '15', '40'],
      correct: 0,
      explanation: '§(x/100) × 200 = 50§ → §2x = 50§ → §x = 25§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'A mixture contains milk and water in the ratio 7:3. If there are 21 liters of milk, how much water is there?',
      options: ['9 liters', '6 liters', '12 liters', '15 liters', '3 liters'],
      correct: 0,
      explanation:
        'If milk (7 parts) = 21 liters, then 1 part = 3 liters. Water (3 parts) = §3 × 3 = 9§ liters.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'If 40% of a number is 80, what is 25% of the same number?',
      options: ['50', '60', '70', '80'],
      correct: 0,
      explanation:
        'If 40% of x = 80, then x = 80/0.4 = 200. So 25% of 200 = §0.25 × 200 = 50§',
    },
    {
      type: 'untimed',
      question:
        'A price increases from $50 to $65. What is the percentage increase?',
      options: ['15%', '20%', '25%', '30%'],
      correct: 3,
      explanation:
        'Increase = $65 - $50 = $15. Percentage increase = §(15/50) × 100 = 30%§',
    },
    {
      type: 'untimed',
      question:
        'In a class of 35 students, the ratio of boys to girls is 3:4. How many girls are there?',
      options: ['15', '20', '21', '28'],
      correct: 1,
      explanation:
        'Total ratio parts: §3 + 4 = 7§. Each part = §35/7 = 5§ students. Girls = 4 parts × 5 = 20.',
    },
    {
      type: 'untimed',
      question: 'What percentage is equivalent to the fraction §7/8§?',
      options: ['82.5%', '85%', '87.5%', '90%'],
      correct: 2,
      explanation: '§7/8 = 0.875 = 87.5%§',
    },
    {
      type: 'untimed',
      question:
        'If a recipe calls for ingredients in the ratio 2:3:4, and you use 6 cups of the first ingredient, how many total cups do you need?',
      options: ['18', '24', '27', '30'],
      correct: 2,
      explanation:
        'If first ingredient (2 parts) = 6 cups, then each part = 3 cups. Total = (2+3+4) × 3 = 27 cups.',
    },
    {
      type: 'untimed',
      question: 'What is 15% of 80?',
      options: ['12', '10', '15', '20', '8'],
      correct: 0,
      explanation: '§15% × 80 = 0.15 × 80 = 12§.',
    },
    {
      type: 'untimed',
      question: 'If §x%§ of 200 is 50, what is §x§?',
      options: ['25', '20', '30', '15', '40'],
      correct: 0,
      explanation: '§(x/100) × 200 = 50§ → §2x = 50§ → §x = 25§.',
    },
    {
      type: 'untimed',
      question:
        'A mixture contains milk and water in the ratio 7:3. If there are 21 liters of milk, how much water is there?',
      options: ['9 liters', '6 liters', '12 liters', '15 liters', '3 liters'],
      correct: 0,
      explanation:
        'If milk (7 parts) = 21 liters, then 1 part = 3 liters. Water (3 parts) = §3 × 3 = 9§ liters.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'A price increases by 20% and then decreases by 20%. Compare the quantities.',
      quantityA: 'Final price',
      quantityB: 'Original price',
      correct: 1,
      explanation:
        'Compare the two quantities directly. Quantity B is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which ratios are equivalent to §3:4§? Select all that apply.',
      options: ['§6:8§', '§9:12§', '§12:15§', '§15:20§', '§18:27§'],
      correct: [0, 1, 3],
      explanation:
        'Equivalent ratios keep the same multiplier: §6:8§, §9:12§, and §15:20§ all reduce to §3:4§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question:
        'A shirt costs $80 after a 20% discount. What was the original price?',
      correctAnswer: '100',
      acceptedAnswers: ['100'],
      explanation:
        'If the sale price is 80% of the original, §0.8x = 80§, so §x = 100§.',
    },
  ];

  const quickReference = [
    'Percent change = §\\left[ \\frac{New - Old}{Old} \\right] \\times 100§%',
    'Ratio a:b means §a/b§',
    'Proportion: §a/b = c/d§',
    'Part = (Percent / 100) × Whole',
    'Successive changes: multiply §(1 + r_1)(1 + r_2)§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Percentage and Ratio concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Percentage and Ratio concepts for the GRE Quantitative section',
    aicontext:
      'Master percentage calculations, ratio problems, proportion solving, and percent change',
  };

  return (
    <LessonTemplate
      lessonId="percentage-ratio"
      lessonTitle="Percentages & Ratios"
      lessonIcon={<FiPercent />}
      lessonDescription="Master percentage calculations, ratios, proportions, and their real-world applications"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="percentage-ratio" />
      }
    />
  );
};

export default PercentageRatioLesson;
